import { useEditModeToggle } from '../hooks/useEditModeToggle'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useLightMode } from '@/hooks/useLightMode'
import { usePermissions } from '@/hooks/usePermissions'
import { useTranslation } from 'react-i18next'
import { HiLockClosed, HiLockOpen } from 'react-icons/hi'

import { Box, useToast } from '@chakra-ui/react'

import { ConfirmEditModeDialog } from '@/components/layouts/EditMode'
import { HeaderDrawerToggle } from '@/components/ui'

interface EditModeToggleIProps {
  facilityId?: string
}

const EDIT_MODE_TOAST_ID = 'edit-mode-toast'

const EditModeToggleControl = ({ facilityId }: EditModeToggleIProps) => {
  const { t } = useTranslation()
  const toast = useToast()
  const mode = useLightMode()

  const showEnteredToast = () => {
    if (toast.isActive(EDIT_MODE_TOAST_ID)) return
    toast({
      id: EDIT_MODE_TOAST_ID,
      position: 'top',
      duration: 2000,
      render: () => (
        <Box
          bg={`bg.card-elevated.${mode}`}
          borderRadius='5px'
          color={`text.primary.${mode}`}
          fontWeight='bold'
          letterSpacing='-0.4px'
          mt='75px'
          py='2'
          textAlign='center'
        >
          {t('floors:header_edit_mode_entered')}
        </Box>
      ),
    })
  }

  const { isEditMode, isConfirmOpen, onConfirmClose, onToggleClick } =
    useEditModeToggle({ facilityId, onEnterEditMode: showEnteredToast })

  return (
    <>
      <ConfirmEditModeDialog isOpen={isConfirmOpen} onClose={onConfirmClose} />
      <HeaderDrawerToggle
        count={0}
        dataTestId='floorPlanPage_header_editMode'
        hasCount={false}
        icon={
          // Ok to keep hardcoded as HeaderDrawerToggle is the same way, and hopefully Edit Mode is fully gone soon...
          isEditMode ? (
            <HiLockOpen color='#1a202c' size='28px' />
          ) : (
            <HiLockClosed color='#1a202c' size='28px' />
          )
        }
        isActive={isEditMode}
        onClick={onToggleClick}
        title={t('floors:header_edit_mode_toggle')}
      />
    </>
  )
}

/**
 * Edit Mode toggle for the Floor-plan header. It used to live at the bottom of
 * the Side Bar; placing and moving Devices on the plan is the one flow that
 * still needs an explicit Edit Mode, so the control now sits with it.
 *
 * The move only happens behind the `removeEditMode` feature flag — while that
 * is off the Side Bar keeps its toggle, and rendering here too would give the
 * app two controls for one piece of Redux state.
 */
export const EditModeToggle = ({ facilityId }: EditModeToggleIProps) => {
  const isRemoveEditModeEnabled = useFeatureFlag(FeatureFlag.removeEditMode)
  const { shouldShowEditMode } = usePermissions()

  if (!isRemoveEditModeEnabled || !shouldShowEditMode) return null

  return <EditModeToggleControl facilityId={facilityId} />
}
